import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Panel, PageHeader, Th, Td, Empty, StatusPill } from "@/components/admin/parts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const emptyArticle = (categoryId) => ({
  category_id: categoryId || "", title: "", short_answer: "", body: [], steps: [],
  keywords: [], related_article_ids: [], feature_gate: "", published: true,
});
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function FaqCms() {
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [editing, setEditing] = useState(null); // article object or null
  const [isNew, setIsNew] = useState(false);
  const [preview, setPreview] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => adminApi.faq().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const articles = data ? (activeCat === "all" ? data.articles : data.articles.filter((a) => a.category_id === activeCat)) : [];
  const catTitle = (id) => data?.categories.find((c) => c.id === id)?.title || id;

  const addCategory = async () => {
    const title = window.prompt("Nazwa nowej kategorii:");
    if (!title) return;
    try { await adminApi.faqCategoryCreate({ title }); toast.success("Dodano kategorię."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(emptyArticle(activeCat !== "all" ? activeCat : (data?.categories[0]?.id || ""))); setIsNew(true); };
  const openEdit = (a) => { setEditing({ ...a }); setIsNew(false); };

  const save = async () => {
    if (!editing.title.trim() || !editing.category_id) { toast.error("Podaj tytuł i kategorię."); return; }
    setSaving(true);
    const payload = {
      category_id: editing.category_id, title: editing.title, short_answer: editing.short_answer,
      body: editing.body, steps: editing.steps, keywords: editing.keywords,
      related_article_ids: editing.related_article_ids, feature_gate: editing.feature_gate || null,
      published: editing.published,
    };
    try {
      if (isNew) await adminApi.faqArticleCreate(payload);
      else await adminApi.faqArticleUpdate(editing.id, payload);
      toast.success("Zapisano pytanie.");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (a) => {
    try { await adminApi.faqArticleUpdate(a.id, { category_id: a.category_id, title: a.title, published: !a.published }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await adminApi.faqArticleDelete(toDelete.id); toast.success("Usunięto pytanie."); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="FAQ i pomoc"
        subtitle={data ? `${data.categories.length} kategorii · ${data.articles.length} pytań` : ""}
        action={
          <div className="flex gap-2">
            <button data-testid={ADMIN.faqAddCategory} onClick={addCategory} className="inline-flex h-10 items-center gap-1.5 rounded-field border border-brand-line px-3 text-sm text-brand-text hover:border-brand-accent/50"><Plus size={15} /> Kategoria</button>
            <button data-testid={ADMIN.faqAddArticle} onClick={openNew} className="inline-flex h-10 items-center gap-1.5 rounded-field bg-brand-accent px-4 text-sm font-semibold text-brand-on"><Plus size={16} /> Pytanie</button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setActiveCat("all")} className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${activeCat === "all" ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>Wszystkie</button>
        {data?.categories.map((c) => (
          <button key={c.id} onClick={() => setActiveCat(c.id)} className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${activeCat === c.id ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec hover:text-brand-text"}`}>{c.title}</button>
        ))}
      </div>

      <Panel>
        {!data ? <Empty>Ładowanie…</Empty> : articles.length === 0 ? <Empty>Brak pytań w tej kategorii.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead><tr className="border-b border-brand-line"><Th>Pytanie</Th><Th>Kategoria</Th><Th>Gate</Th><Th>Status</Th><Th className="text-right">Akcje</Th></tr></thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} data-testid={ADMIN.faqArticleRow} className="border-b border-brand-line/60">
                    <Td><div className="font-medium text-brand-text">{a.title}</div><div className="max-w-[360px] truncate text-xs text-brand-muted">{a.short_answer}</div></Td>
                    <Td className="text-brand-sec">{catTitle(a.category_id)}</Td>
                    <Td>{a.feature_gate ? <StatusPill status="W_TOKU" label={a.feature_gate} /> : <span className="text-xs text-brand-muted">—</span>}</Td>
                    <Td><button data-testid={ADMIN.faqPublishToggle} onClick={() => togglePublish(a)}><StatusPill status={a.published ? "settled" : "ZAMKNIETA"} label={a.published ? "OPUBLIKOWANE" : "SZKIC"} /></button></Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setPreview(a)} className="rounded-field border border-brand-line p-2 text-brand-sec hover:text-brand-text" title="Podgląd"><Eye size={15} /></button>
                        <button onClick={() => openEdit(a)} className="rounded-field border border-brand-line p-2 text-brand-sec hover:text-brand-text" title="Edytuj"><Pencil size={15} /></button>
                        <button onClick={() => setToDelete(a)} className="rounded-field border border-brand-line p-2 text-red-300 hover:border-red-400/50" title="Usuń"><Trash2 size={15} /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{isNew ? "Nowe pytanie" : "Edycja pytania"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
              <L label="Tytuł"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp} /></L>
              <div className="grid grid-cols-2 gap-3">
                <L label="Kategoria">
                  <select value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} className={inp}>
                    {data?.categories.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </L>
                <L label="Feature gate (opcjonalnie)"><input value={editing.feature_gate || ""} onChange={(e) => setEditing({ ...editing, feature_gate: e.target.value })} placeholder="np. ai-text" className={inp} /></L>
              </div>
              <L label="Krótka odpowiedź"><textarea rows={2} value={editing.short_answer} onChange={(e) => setEditing({ ...editing, short_answer: e.target.value })} className={inp} /></L>
              <L label="Treść (jeden akapit na linię)"><textarea rows={3} value={toLines(editing.body)} onChange={(e) => setEditing({ ...editing, body: fromLines(e.target.value) })} className={inp} /></L>
              <L label="Kroki (jeden na linię)"><textarea rows={2} value={toLines(editing.steps)} onChange={(e) => setEditing({ ...editing, steps: fromLines(e.target.value) })} className={inp} /></L>
              <div className="grid grid-cols-2 gap-3">
                <L label="Słowa kluczowe (przecinki)"><input value={toCsv(editing.keywords)} onChange={(e) => setEditing({ ...editing, keywords: fromCsv(e.target.value) })} className={inp} /></L>
                <L label="Powiązane pytania (id, przecinki)"><input value={toCsv(editing.related_article_ids)} onChange={(e) => setEditing({ ...editing, related_article_ids: fromCsv(e.target.value) })} className={inp} /></L>
              </div>
              <label className="flex items-center gap-2 text-sm text-brand-text">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Opublikowane
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEditing(null)} className="h-10 rounded-field border border-brand-line px-4 text-sm text-brand-sec">Anuluj</button>
                <button data-testid={ADMIN.faqEditSave} onClick={save} disabled={saving} className="h-10 rounded-field bg-brand-accent px-5 text-sm font-semibold text-brand-on disabled:opacity-60">{saving ? "Zapisywanie…" : "Zapisz"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{preview?.title}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p className="text-brand-text">{preview.short_answer}</p>
              {(preview.body || []).map((p, i) => <p key={i} className="text-brand-sec">{p}</p>)}
              {(preview.steps || []).length > 0 && (
                <ol className="list-decimal space-y-1 pl-5 text-brand-sec">{preview.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              )}
              <p className="text-xs text-brand-muted">Deep-link: /pomoc#{preview.id}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Usuń pytanie" description={`Czy na pewno usunąć „${toDelete?.title}"?`} confirmLabel="Usuń" danger onConfirm={doDelete} testId="admin-faq-delete" />
    </div>
  );
}

const inp = "w-full rounded-field border border-brand-line bg-brand-surface px-3 py-2 text-sm text-brand-text outline-none focus:border-brand-accent/60";
const L = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-muted">{label}</label>
    {children}
  </div>
);
